import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@/components/common/MainContentHeader';
import useI18n from '../hooks/useI18n';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '@/context/UserContext';
import { Button } from '@/components/common/Button';
import InfluencersTabComponent from '@/components/common/campaign/InfluencersTabComponent';
import CampaignTabComponent from '@/components/common/campaign/CampaignTabComponent';
import { CalendarFilter } from '@/components/common/CalendarFilter/CalendarFilter';
import LastUpdatedBadge from '@/components/common/LastUpdatedBadge';
import { useCampaignAnalysisQuery } from '@/hooks/queries/useAnalyticsQueries';
import { useDateRangeQueryParams } from '@/hooks/useDateRangeQueryParams';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useForceRefresh } from '@/hooks/useForceRefresh';
import RefreshButton from '@/components/common/RefreshButton';

const AnalyticsCampaign: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<'campaign' | 'creators'>('campaign');

  const { t } = useI18n();
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;

  const brandId = user?.brand?._id || '';
  const isRealUser = user?.role === 'brandUser' && !isDummyUser && !!brandId;

  const [dateFilteredData, setDateFilteredData] = useState<{
    startDate: Date | null;
    endDate: Date | null;
    filter: string;
  } | null>(null);

  const {
    filter: chartFilter,
    customStartDate,
    customEndDate,
  } = useDateRangeQueryParams(dateFilteredData ?? undefined);

  const { data: campaignAnalysisData } = useCampaignAnalysisQuery(
    brandId,
    isRealUser,
    chartFilter,
    customStartDate,
    customEndDate,
  );

  const queryClient = useQueryClient();
  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    () =>
      queryClient.refetchQueries({
        queryKey: ['analytics', 'brand'],
        type: 'active',
      }),
    `brand-analytics:${brandId}`,
  );

  // Dummy users have no analysis query (it's disabled), so there's no real
  // `lastRefreshedAt`. Show today's date pinned to 6:00 AM local so the badge
  // still renders for the demo experience.
  const dummyLastRefreshedAt = React.useMemo(() => {
    const d = new Date();
    d.setHours(6, 0, 0, 0);
    return d.toISOString();
  }, []);

  // Read the tab from URL query parameter on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab === 'creators') {
      setViewMode('creators');
    } else {
      setViewMode('campaign'); // Default to 'campaign' if tab is invalid or missing
    }
  }, [location.search]);

  const handleDateFilterChange = useCallback(
    (startDate: Date | null, endDate: Date | null, filter: string) => {
      setDateFilteredData({ startDate, endDate, filter });
    },
    [],
  );

  // Update URL and viewMode when a tab is clicked
  const handleTabChange = (mode: 'campaign' | 'creators') => {
    setViewMode(mode);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', mode);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const formattedEndDate = dateFilteredData?.endDate
    ? format(dateFilteredData.endDate, 'dd/MM/yyyy')
    : '';

  const formattedStartDate = dateFilteredData?.startDate
    ? format(dateFilteredData.startDate, 'dd/MM/yyyy')
    : '';
  return (
    <div className="bg-white h-[calc(100%-152px)]">
      <div className="p-[24px] lg:bg-[#F0F5FF] bg-white">
        <div className="flex items-start justify-between gap-4">
          <Header title="" subtitle={String(t('common.analytics'))} />
          <div className="flex items-center gap-3">
            <LastUpdatedBadge
              lastRefreshedAt={
                isDummyUser
                  ? dummyLastRefreshedAt
                  : campaignAnalysisData?.lastRefreshedAt
              }
            />
            {!isDummyUser && (
              <RefreshButton
                onRefresh={refresh}
                isRefreshing={isRefreshing}
                secondsRemaining={secondsRemaining}
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between w-full xs:w-auto mt-4">
          <div className="flex items-center gap-2">
            <Button
              variant="filled"
              className={`w-full xs:w-[176px] mt-2 md:mt-0 ${
                viewMode === 'campaign' ? 'bg-white' : 'bg-transparent'
              } text-base font-normal text-[#020D3A]`}
              onClick={() => handleTabChange('campaign')}
            >
              {t('common.campaign')}
            </Button>
            <Button
              variant="filled"
              className={`w-full xs:w-[200px] mt-2 md:mt-0 ${
                viewMode === 'creators' ? 'bg-white' : 'bg-transparent'
              } text-base font-normal text-[#020D3A]`}
              onClick={() => handleTabChange('creators')}
            >
              {t('common.creators')}
            </Button>
          </div>
          <CalendarFilter
            onDateRangeChange={handleDateFilterChange}
            defaultFilter="all-time"
          />
        </div>
      </div>
      <div className="bg-white p-[12px] sm:p-[24px]">
        {viewMode === 'campaign' ? (
          <CampaignTabComponent
            formattedEndDate={formattedEndDate}
            formattedStartDate={formattedStartDate}
            dateFilteredData={dateFilteredData}
          />
        ) : (
          <InfluencersTabComponent dateFilteredData={dateFilteredData} />
        )}
      </div>
    </div>
  );
};

export default AnalyticsCampaign;
